import React, { useCallback, useEffect, useState } from 'react';
import { ImageIcon, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../../lib/api/core';
import { cmsPartnerApi, type CmsPartner, type CmsPartnerInput } from '../../../../lib/api/cms';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Textarea } from '../../../../components/ui/textarea';
import { RichTextEditor } from '../../../../components/ui/rich-text-editor';
import { Switch } from '../../../../components/ui/switch';
import { Label } from '../../../../components/ui/label';
import { Card, CardContent } from '../../../../components/ui/card';
import LoeschenMitGrundDialog from '../../../../components/ui/loeschen-mit-grund-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { MediaPicker } from '../../../../components/cms/MediaPicker';

interface StandortItem {
  id: number;
  name: string;
}

const EMPTY_DRAFT: CmsPartnerInput = {
  name: '',
  logo_url: '',
  description: '',
  address: '',
  phone: '',
  email: '',
  website: '',
  is_active: true,
  show_on_homepage: false,
  standort_ids: [],
  sort_order: 0,
  benefits: [],
  disclaimer: '',
};

/** True for an empty value or a syntactically valid http(s) URL. */
function isValidWebsite(value: string | null | undefined): boolean {
  const s = (value ?? '').trim();
  if (!s) return true;
  try {
    const url = new URL(/^https?:\/\//i.test(s) ? s : `https://${s}`);
    return url.hostname.includes('.');
  } catch {
    return false;
  }
}

const CmsPartnersPage: React.FC = () => {
  const [partners, setPartners] = useState<CmsPartner[]>([]);
  const [standorte, setStandorte] = useState<StandortItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CmsPartner | null>(null);
  const [draft, setDraft] = useState<CmsPartnerInput>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [zuLoeschen, setZuLoeschen] = useState<CmsPartner | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        cmsPartnerApi.list(),
        api.get<StandortItem[]>('/api/standorte?active_only=false'),
      ]);
      setPartners(p);
      setStandorte(Array.isArray(s) ? s : []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Partner konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
    setOpen(true);
  };

  const openEdit = (p: CmsPartner) => {
    setEditing(p);
    setDraft({
      name: p.name,
      logo_url: p.logo_url ?? '',
      description: p.description ?? '',
      address: p.address ?? '',
      phone: p.phone ?? '',
      email: p.email ?? '',
      website: p.website ?? '',
      is_active: p.is_active,
      show_on_homepage: p.show_on_homepage,
      standort_ids: p.standort_ids,
      sort_order: p.sort_order,
      benefits: p.benefits ?? [],
      disclaimer: p.disclaimer ?? '',
    });
    setOpen(true);
  };

  const set = (key: keyof CmsPartnerInput, value: unknown) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const toggleStandort = (id: number) =>
    setDraft((d) => {
      const ids = d.standort_ids || [];
      return { ...d, standort_ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] };
    });

  const save = async () => {
    if (!draft.name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }
    if (!isValidWebsite(draft.website)) {
      toast.error('Bitte eine gültige Website-URL angeben (z. B. https://…)');
      return;
    }
    setSaving(true);
    try {
      if (editing) await cmsPartnerApi.update(editing.id, draft);
      else await cmsPartnerApi.create(draft);
      setOpen(false);
      await load();
      toast.success(editing ? 'Partner gespeichert' : 'Partner erstellt');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const quickToggle = async (p: CmsPartner, key: 'is_active', value: boolean) => {
    setPartners((prev) => prev.map((x) => (x.id === p.id ? { ...x, [key]: value } : x)));
    try {
      await cmsPartnerApi.update(p.id, { [key]: value });
    } catch (e) {
      setPartners((prev) => prev.map((x) => (x.id === p.id ? { ...x, [key]: !value } : x)));
      toast.error(e instanceof Error ? e.message : 'Änderung fehlgeschlagen');
    }
  };

  const remove = (p: CmsPartner) => setZuLoeschen(p);

  const loeschen = async (p: CmsPartner, grund: string) => {
    await cmsPartnerApi.remove(p.id, grund);
    setPartners((prev) => prev.filter((x) => x.id !== p.id));
    setZuLoeschen(null);
    toast.success('Partner gelöscht');
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">FamilyCard-Partner</h1>
          <p className="text-sm text-muted-foreground">
            Partner zentral verwalten und Standorten bzw. der Startseite zuordnen.
          </p>
        </div>
        <Button type="button" onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" /> Neuer Partner
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : partners.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Noch keine Partner. Lege den ersten an.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="divide-y p-0">
            {partners.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-4 px-4 py-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded border bg-white">
                  {p.logo_url ? (
                    <img src={p.logo_url} alt="" className="h-full w-full object-contain" />
                  ) : (
                    <ImageIcon className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{p.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {p.standort_ids.length === 0
                      ? 'Alle Standorte'
                      : `${p.standort_ids.length} Standort(e)`}
                  </div>
                </div>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Switch
                    checked={p.is_active}
                    onCheckedChange={(v) => quickToggle(p, 'is_active', v)}
                  />
                  Aktiv
                </label>
                <Button type="button" variant="outline" size="sm" onClick={() => openEdit(p)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => remove(p)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="xl" className="max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Partner: ${editing.name}` : 'Neuer Partner'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={draft.name} onChange={(e) => set('name', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Logo</Label>
              <div className="flex items-center gap-3">
                <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded border bg-white">
                  {draft.logo_url ? (
                    <img src={draft.logo_url} alt="" className="h-full w-full object-contain" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
                  Aus Bibliothek
                </Button>
                {draft.logo_url && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => set('logo_url', '')}>
                    Entfernen
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Angebot / Beschreibung</Label>
              <RichTextEditor value={draft.description ?? ''} onChange={(html) => set('description', html)} />
            </div>
            <div className="space-y-1.5">
              <Label>Vorteile</Label>
              <div className="space-y-2">
                {(draft.benefits || []).map((benefit, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={benefit}
                      placeholder="Vorteil"
                      onChange={(e) =>
                        set('benefits', (draft.benefits || []).map((x, idx) => (idx === i ? e.target.value : x)))
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0 text-destructive hover:text-destructive"
                      onClick={() => set('benefits', (draft.benefits || []).filter((_, idx) => idx !== i))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => set('benefits', [...(draft.benefits || []), ''])}
                >
                  <Plus className="mr-2 h-4 w-4" /> Vorteil hinzufügen
                </Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Disclaimer</Label>
              <Textarea
                value={draft.disclaimer ?? ''}
                onChange={(e) => set('disclaimer', e.target.value)}
                rows={2}
                placeholder="Kleingedrucktes unterhalb der Vorteile, z. B. Einschränkungen des Angebots."
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Adresse</Label>
                <Input value={draft.address ?? ''} onChange={(e) => set('address', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Telefon</Label>
                <Input value={draft.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>E-Mail</Label>
                <Input value={draft.email ?? ''} onChange={(e) => set('email', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Website</Label>
                <Input type="url" placeholder="https://…" value={draft.website ?? ''} onChange={(e) => set('website', e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Standorte (leer = alle)</Label>
              <div className="flex flex-wrap gap-2">
                {standorte.map((s) => {
                  const active = (draft.standort_ids || []).includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleStandort(s.id)}
                      className={`rounded-full border px-3 py-1 text-sm ${
                        active ? 'border-primary bg-primary/10 text-primary' : 'border-input text-muted-foreground'
                      }`}
                    >
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={draft.is_active !== false} onCheckedChange={(v) => set('is_active', v)} />
                Aktiv
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button type="button" loading={saving} onClick={save}>
              Speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(url) => set('logo_url', url)}
      />
      <LoeschenMitGrundDialog
        offen={zuLoeschen !== null}
        titel={`Partner „${zuLoeschen?.name ?? ''}“ löschen?`}
        onAbbrechen={() => setZuLoeschen(null)}
        onLoeschen={async (grund) => {
          if (zuLoeschen) await loeschen(zuLoeschen, grund);
        }}
      />
    </div>
  );
};

export default CmsPartnersPage;
